import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getTimeWindowStatus as getTimeWindowStatusPure } from './useAttendanceCheckIn/timeWindow';

// Container hook for AttendanceCheckIn — all state/effects/handlers.
// Extracted VERBATIM (second-pass: split the Chicago-timezone-aware
// check-in-window calculation into useAttendanceCheckIn/timeWindow.js).
export default function useAttendanceCheckIn() {
  console.log('🔍 AttendanceCheckIn component function called');
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  console.log('🔍 AttendanceCheckIn component loaded with sessionId:', sessionId);
  console.log('🔍 Current URL:', window.location.href);
  console.log('🔍 User state:', user);
  console.log('🔍 Auth loading:', authLoading);
  const [nowTick, setNowTick] = useState(Date.now());
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [attendanceStatus, setAttendanceStatus] = useState(null);

  const getTimeWindowStatus = () => getTimeWindowStatusPure(session, nowTick);

  // Redirect to login if not authenticated
  useEffect(() => {
    console.log('🔍 AttendanceCheckIn - Auth state:', { user, authLoading, sessionId });
    if (!authLoading && !user) {
      const currentPath = `/attendance/checkin/${sessionId}`;
      console.log('🔍 Redirecting to login with path:', currentPath);
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
    } else if (user) {
      console.log('🔍 User is authenticated, proceeding with check-in');
    }
  }, [user, authLoading, navigate, sessionId]);

  const fetchSessionData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await apiService.getAttendanceSession(sessionId);

      if (response.success) {
        setSession(response.session);

        console.log('🔍 Session data loaded:', {
          session: response.session,
          checkinStart: response.session.checkin_start_time,
          checkinEnd: response.session.checkin_end_time,
          hasTimeWindow: !!(response.session.checkin_start_time && response.session.checkin_end_time)
        });

        // Check if student is already checked in
        const studentRecord = response.session.records.find(
          record => record.student_id === apiService.getCurrentUser()?.id
        );

        if (studentRecord) {
          setAttendanceStatus(studentRecord.status);
        }

        // No automatic check-in validation - let the user manually check in when they click the button
      } else {
        setError(response.message || 'Session not found');
      }
    } catch (error) {
      console.error('Error fetching session:', error);
      setError('Failed to load attendance session');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId && user) {
      fetchSessionData();
    }
  }, [sessionId, fetchSessionData, user]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setNowTick(Date.now());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const handleCheckIn = async (status = 'present') => {
    try {
      const timeWindowStatus = getTimeWindowStatus();
      if (timeWindowStatus && timeWindowStatus.type === 'before') {
        // Keep the live countdown banner visible (it updates every second).
        setError('');
        setSuccess('');
        return;
      }

      if (timeWindowStatus && timeWindowStatus.type === 'after') {
        setError(timeWindowStatus.message);
        return;
      }

      setCheckingIn(true);
      setError('');
      setSuccess('');

      console.log('🔍 Attempting to check in:', { sessionId, status, userId: user?.id });

      const response = await apiService.checkInAttendance(sessionId, status);

      console.log('🔍 Check-in response:', response);
      console.log('🔍 Check-in response details:', JSON.stringify(response, null, 2));

      if (response.success) {
        setSuccess('Successfully checked in for attendance!');
        setAttendanceStatus(status);

        // Redirect after successful check-in
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        console.error('🔍 Check-in failed:', response);
        console.error('🔍 Check-in failed details:', JSON.stringify(response, null, 2));
        const responseMessage = response.message || 'Failed to check in';
        if (
          responseMessage.includes('Check-in opens in') ||
          responseMessage.includes('Check-in window is available during') ||
          responseMessage.includes('Check-in window has closed') ||
          responseMessage.includes('Check-in is not available at this time')
        ) {
          const liveStatus = getTimeWindowStatus();
          if (liveStatus?.type === 'before') {
            // Keep live countdown panel visible instead of static error text.
            setError('');
            return;
          }

          if (liveStatus?.type === 'after') {
            setError(liveStatus.message);
            return;
          }
        }

        setError(responseMessage);
      }
    } catch (error) {
      console.error('🔍 Error checking in:', error);
      console.error('🔍 Error details:', {
        message: error.message,
        status: error.status,
        response: error.response
      });
      // Show the actual error message from the server with better formatting
      let errorMessage = error.message || 'Failed to check in. Please try again.';

      // Temporary debugging - show the raw error message
      console.log('🔍 RAW ERROR MESSAGE:', errorMessage);

      console.log('🔍 Error message formatting:', {
        originalMessage: error.message,
        sessionExists: !!session,
        checkinStartTime: session?.checkin_start_time
      });

      // If it's a time window error, use the server's formatted message directly
      if (error.message && (
        error.message.includes('Check-in is not available at this time') ||
        error.message.includes('Check-in opens in') ||
        error.message.includes('Check-in window is available during') ||
        error.message.includes('Check-in window has closed') ||
        error.message.includes('Check-in is not available. This session is for')
      )) {
        const liveStatus = getTimeWindowStatus();
        if (liveStatus?.type === 'before') {
          // Keep live countdown panel visible instead of static error text.
          setError('');
          setCheckingIn(false);
          return;
        }

        if (liveStatus?.type === 'after') {
          errorMessage = liveStatus.message;
        } else {
          // Fall back to server message when client-side status can't be derived.
          errorMessage = error.message;
        }

        console.log('🔍 Using formatted time window error message:', errorMessage);
      }

      setError(errorMessage);
    } finally {
      setCheckingIn(false);
    }
  };

  const getStatusMessage = () => {
    if (attendanceStatus === 'present') {
      return 'You have already checked in as present for this session.';
    } else if (attendanceStatus === 'late') {
      return 'You have already checked in as late for this session.';
    }
    // Don't show message for 'absent' status as it's the default
    return null;
  };

  const getStatusColor = () => {
    if (attendanceStatus === 'present') return 'success';
    if (attendanceStatus === 'late') return 'info';
    if (attendanceStatus === 'absent') return 'info'; // Changed from 'error' to 'info'
    return 'info';
  };

  return {
    user,
    authLoading,
    navigate,
    session,
    loading,
    error,
    success,
    attendanceStatus,
    checkingIn,
    getTimeWindowStatus,
    getStatusMessage,
    getStatusColor,
    handleCheckIn,
  };
}
